import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authRepository } from '../infrastructure/auth.repository';
import { otpSession } from '../infrastructure/otpSession';
import { apiErrorMessage } from './apiError';

export type OtpStep = 'request' | 'verify';

/**
 * UC-5: máquina request/verify. R-3 (preserva B-2 as-is): tras `verifyOtp` exitoso persiste la
 * sesión vía `otpSession` (localStorage) y navega — este hook NO importa ni llama a
 * `@modules/iam` (`login`/`setUser`/`refreshUser`), a propósito: `AuthContext` no se
 * sincroniza en memoria, igual que el legado. La navegación a `/dashboard` la decide
 * `presentation/` al recibir `verified === true` de `verifyCode()`.
 */
export function useOtpVerification() {
  const { t } = useTranslation();
  const [step, setStep] = useState<OtpStep>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const requestCode = useCallback(async (): Promise<void> => {
    if (!email.trim()) {
      setError(t('auth.emailRequired'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await authRepository.requestOtp(email);
      setSuccess(t('auth.otpSent'));
      setStep('verify');
    } catch (err: unknown) {
      setError(apiErrorMessage(err) || t('auth.requestOtpError'));
    } finally {
      setLoading(false);
    }
  }, [email, t]);

  const verifyCode = useCallback(async (): Promise<boolean> => {
    if (!code.trim()) {
      setError(t('auth.otpRequired'));
      return false;
    }
    setLoading(true);
    setError('');
    try {
      const session = await authRepository.verifyOtp(email, code);
      otpSession.persist(session);
      return true;
    } catch (err: unknown) {
      setError(apiErrorMessage(err) || t('auth.otpInvalid'));
      return false;
    } finally {
      setLoading(false);
    }
  }, [email, code, t]);

  const requestNewCode = useCallback((): void => {
    setStep('request');
    setCode('');
    setError('');
    setSuccess('');
  }, []);

  return {
    step,
    email,
    setEmail,
    code,
    setCode,
    error,
    success,
    loading,
    requestCode,
    verifyCode,
    requestNewCode,
  };
}
